/**
 * Enhanced Message Input Component
 * Supports text, emojis, and file attachments
 */

import React, { useState, useRef, useEffect } from 'react';
import { 
  Send, 
  Paperclip, 
  Image, 
  Smile, 
  X, 
  Loader,
  File,
  Camera,
  Mic,
  AlertCircle
} from 'lucide-react';
import { toast } from 'react-hot-toast';

// Common emoji categories and emojis
const EMOJI_CATEGORIES = {
  'Smileys & Emotion': [
    '😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '🙃',
    '😉', '😊', '😇', '🥰', '😍', '🤩', '😘', '😗', '😚', '😙',
    '😋', '😛', '😜', '🤪', '😝', '🤑', '🤗', '🤭', '🤫', '🤔',
    '🤐', '🤨', '😐', '😑', '😶', '😏', '😒', '🙄', '😬', '🤥',
    '😔', '😪', '🤤', '😴', '😷', '🤒', '🤕', '🤢', '🤮', '🤧',
    '🥵', '🥶', '🥴', '😵', '🤯', '🤠', '🥳', '😎', '🤓', '🧐'
  ],
  'Gestures & Body': [
    '👍', '👎', '👌', '🤌', '🤏', '✌️', '🤞', '🤟', '🤘', '🤙',
    '👈', '👉', '👆', '🖕', '👇', '☝️', '👋', '🤚', '🖐', '✋',
    '🖖', '👏', '🙌', '🤲', '🤝', '🙏', '✍️', '💪', '🦾', '🦿'
  ],
  'Activities & Objects': [
    '⚽', '🏀', '🏈', '⚾', '🥎', '🎾', '🏐', '🏉', '🎱', '🪀',
    '🏓', '🏸', '🏒', '🏑', '🥍', '🏏', '🪃', '🥅', '⛳', '🪁',
    '🎯', '🎱', '🔮', '🪄', '🎮', '🕹️', '🎰', '🎲', '🧩', '🃏'
  ],
  'Food & Drink': [
    '🍎', '🍐', '🍊', '🍋', '🍌', '🍉', '🍇', '🍓', '🫐', '🍈',
    '🍒', '🍑', '🥭', '🍍', '🥥', '🥝', '🍅', '🍆', '🥑', '🥦',
    '🥬', '🥒', '🌶️', '🫑', '🌽', '🥕', '🫒', '🧄', '🧅', '🥔'
  ],
  'Travel & Places': [
    '🚗', '🚕', '🚙', '🚌', '🚎', '🏎️', '🚓', '🚑', '🚒', '🚐',
    '🛻', '🚚', '🚛', '🚜', '🏍️', '🛵', '🚲', '🛴', '🛹', '🛼',
    '🚁', '🛸', '✈️', '🛩️', '🪂', '⛵', '🚤', '🛥️', '🛳️', '⛴️'
  ]
};

/**
 * Enhanced Message Input Component
 * 
 * @param {Object} props - Component props
 * @param {string} props.value - Current message value
 * @param {Function} props.onChange - Value change handler
 * @param {Function} props.onSend - Send message handler
 * @param {Function} props.onFileUpload - File upload handler
 * @param {boolean} props.disabled - Whether input is disabled
 * @param {boolean} props.sending - Whether message is being sent
 * @param {string} props.placeholder - Input placeholder
 * @returns {JSX.Element} Enhanced message input component
 */
const EnhancedMessageInput = ({
  value,
  onChange,
  onSend,
  onFileUpload,
  disabled = false,
  sending = false,
  placeholder = "Type a message..."
}) => {
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const [selectedEmojiCategory, setSelectedEmojiCategory] = useState('Smileys & Emotion');
  const [uploadingFile, setUploadingFile] = useState(false);
  
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const attachmentMenuRef = useRef(null);
  
  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
      if (attachmentMenuRef.current && !attachmentMenuRef.current.contains(event.target)) {
        setShowAttachmentMenu(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Handle emoji selection
  const handleEmojiSelect = (emoji) => {
    const newValue = value + emoji;
    onChange(newValue);
    inputRef.current?.focus();
  };
  
  // Handle file selection
  const handleFileSelect = async (event, fileType) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Validate file size (10MB for documents/images)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error('File size must be less than 10MB');
      return;
    }
    
    // Validate file type
    const allowedTypes = {
      image: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
      document: [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/plain'
      ]
    };
    
    const isValidType = fileType === 'any' || 
      (allowedTypes[fileType] && allowedTypes[fileType].includes(file.type));
    
    if (!isValidType) {
      toast.error(`Invalid file type. Please select a valid ${fileType} file.`);
      return;
    }
    
    try {
      setUploadingFile(true);
      setShowAttachmentMenu(false);
      
      if (onFileUpload) {
        await onFileUpload(file, fileType);
      }
      
      // Clear the input
      event.target.value = '';
      
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(error.message || 'Failed to upload file');
    } finally {
      setUploadingFile(false);
    }
  };
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    if (value.trim() && !disabled && !sending && onSend) {
      onSend(value.trim());
    }
  };
  
  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };
  
  return (
    <div className="relative">
      {/* Emoji Picker */}
      {showEmojiPicker && (
        <div 
          ref={emojiPickerRef}
          className="absolute bottom-full mb-2 left-0 w-80 max-w-full bg-white rounded-lg shadow-lg border border-gray-200 z-50"
        >
          <div className="p-3 border-b border-gray-200">
            <div className="flex space-x-1 overflow-x-auto">
              {Object.keys(EMOJI_CATEGORIES).map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedEmojiCategory(category)}
                  className={`px-2 py-1 text-xs rounded whitespace-nowrap ${
                    selectedEmojiCategory === category
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {category.split(' ')[0]}
                </button>
              ))}
            </div>
          </div>
          
          <div className="p-3 max-h-48 overflow-y-auto">
            <div className="grid grid-cols-8 gap-2">
              {EMOJI_CATEGORIES[selectedEmojiCategory].map((emoji, index) => (
                <button
                  key={index}
                  onClick={() => handleEmojiSelect(emoji)}
                  className="text-xl hover:bg-gray-100 rounded p-1 transition-colors"
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      
      {/* Attachment Menu */}
      {showAttachmentMenu && (
        <div 
          ref={attachmentMenuRef}
          className="absolute bottom-full mb-2 left-0 bg-white rounded-lg shadow-lg border border-gray-200 z-50"
        >
          <div className="p-2">
            <button
              onClick={() => imageInputRef.current?.click()}
              className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
            >
              <Image className="w-4 h-4 mr-3 text-blue-500" />
              Upload Image
            </button>
            
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
            >
              <File className="w-4 h-4 mr-3 text-green-500" />
              Upload Document
            </button>
            
            <button
              onClick={() => {
                toast('Camera feature coming soon!', {
                  icon: '📷',
                  duration: 2000,
                });
                setShowAttachmentMenu(false);
              }}
              className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
            >
              <Camera className="w-4 h-4 mr-3 text-purple-500" />
              Take Photo
            </button>
            
            <button
              onClick={() => {
                toast('Voice message feature coming soon!', {
                  icon: '🎤',
                  duration: 2000,
                });
                setShowAttachmentMenu(false);
              }}
              className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
            >
              <Mic className="w-4 h-4 mr-3 text-red-500" />
              Voice Message
            </button>
          </div>
        </div>
      )}
      
      {/* File Upload Indicator */}
      {uploadingFile && (
        <div className="absolute bottom-full mb-2 left-0 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 flex items-center">
          <Loader className="w-4 h-4 mr-2 animate-spin text-blue-500" />
          <span className="text-sm text-blue-700">Uploading file...</span>
        </div>
      )}
      
      {/* Main Input Form */}
      <form onSubmit={handleSubmit} className="flex items-center space-x-2 p-3 bg-white border-t border-gray-200">
        {/* Attachment Button */}
        <button
          type="button"
          onClick={() => {
            setShowAttachmentMenu(!showAttachmentMenu);
            setShowEmojiPicker(false);
          }}
          disabled={disabled || uploadingFile}
          className={`p-2 rounded-full transition-colors ${
            showAttachmentMenu
              ? 'bg-blue-100 text-blue-600'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
          } disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          <Paperclip size={20} />
        </button>
        
        {/* Emoji Button */}
        <button
          type="button"
          onClick={() => {
            setShowEmojiPicker(!showEmojiPicker);
            setShowAttachmentMenu(false);
          }}
          disabled={disabled}
          className={`p-2 rounded-full transition-colors ${
            showEmojiPicker
              ? 'bg-yellow-100 text-yellow-600'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
          } disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          <Smile size={20} />
        </button>
        
        {/* Text Input */}
        <div className="flex-1 relative">
          <textarea
            ref={inputRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={placeholder}
            disabled={disabled || uploadingFile}
            rows={1}
            className="w-full px-4 py-2 border border-gray-300 rounded-full resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            style={{
              minHeight: '40px',
              maxHeight: '120px'
            }}
            onInput={(e) => {
              // Auto-resize textarea
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
            }}
          />
          
          {disabled && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-75 rounded-full">
              <div className="flex items-center text-gray-500 text-sm">
                <AlertCircle className="w-4 h-4 mr-1" />
                Cannot send messages
              </div>
            </div>
          )}
        </div>
        
        {/* Send Button */}
        <button
          type="submit"
          disabled={!value.trim() || disabled || sending || uploadingFile}
          className={`p-2 rounded-full transition-colors ${
            !value.trim() || disabled || sending || uploadingFile
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
              : 'bg-blue-500 text-white hover:bg-blue-600'
          }`}
        >
          {sending ? (
            <Loader size={20} className="animate-spin" />
          ) : (
            <Send size={20} />
          )}
        </button>
      </form>
      
      {/* Hidden File Inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt"
        onChange={(e) => handleFileSelect(e, 'document')}
        className="hidden"
      />
      
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        onChange={(e) => handleFileSelect(e, 'image')}
        className="hidden"
      />
    </div>
  );
};

export default EnhancedMessageInput;